import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GridModule, GridDataResult } from '@progress/kendo-angular-grid';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { ProgressBarModule } from '@progress/kendo-angular-progressbar';
import { IconsModule } from '@progress/kendo-angular-icons';
import { KENDO_ICONS } from '@progress/kendo-angular-icons';
import { Subscription, forkJoin, of } from 'rxjs';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import { PartDocumentLink, Document, LinkType, DocumentType, FileVaultItem, ChunkUploadInfo, UploadStatus, DrawingNumberResult } from '../../models/file-vault.models';
import { LifecycleBadgeComponent } from '../lifecycle-badge/lifecycle-badge.component';

export interface DocumentLinkActionEvent {
  action: 'view' | 'edit' | 'download' | 'unlink' | 'upload';
  link: PartDocumentLink;
  document?: Document;
}

export interface AddDocumentLinkEvent {
  documentId?: string;
  linkType: LinkType;
  purpose?: string;
}

@Component({
  selector: 'fv-document-links',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    GridModule,
    ButtonsModule,
    DialogModule,
    DropDownsModule,
    InputsModule,
    IndicatorsModule,
    IconsModule,
    ProgressBarModule,
    KENDO_ICONS,
    LifecycleBadgeComponent
  ],
  template: `
    <div class="document-links-container">
      <!-- Toolbar -->
      <div class="toolbar" *ngIf="showToolbar">
        <div class="toolbar-left">
          <span class="title">Linked Documents</span>
          <span class="count" *ngIf="!isLoading">({{ links.length }})</span>
        </div>
        <div class="toolbar-right">
          <button kendoButton [themeColor]="'primary'" (click)="openAddDialog()" [disabled]="!canEdit">
            Link Document
          </button>
          <button kendoButton [look]="'flat'" (click)="refresh()">
            Refresh
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div class="loading" *ngIf="isLoading">
        <kendo-loader [type]="'infinite-spinner'" [size]="'medium'"></kendo-loader>
        <span>Loading linked documents...</span>
      </div>

      <!-- Empty State -->
      <div class="empty" *ngIf="!isLoading && links.length === 0">
        <div class="empty-icon"><kendo-icon name="file" [size]="'xlarge'"></kendo-icon></div>
        <div class="empty-text">No linked documents</div>
        <div class="empty-hint" *ngIf="canEdit">Click "Link Document" to attach documents to this part</div>
      </div>

      <!-- Document Links Grid -->
      <kendo-grid
        *ngIf="!isLoading && links.length > 0"
        [data]="gridData"
        [selectable]="{ mode: 'single' }"
        [sortable]="true"
        [pageable]="links.length > 25"
        [pageSize]="25"
        (selectionChange)="onSelectionChange($event)"
        class="document-links-grid">

        <kendo-grid-column field="linkType" title="Type" [width]="100">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="link-type-badge" [attr.data-type]="dataItem.linkType">
              {{ getLinkTypeLabel(dataItem.linkType) }}
            </span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="document.documentNumber" title="Document Number" [width]="180">
          <ng-template kendoGridCellTemplate let-dataItem>
            <div class="doc-cell">
              <kendo-icon [name]="getDocumentIcon(dataItem.document)" class="doc-icon"></kendo-icon>
              <span class="doc-number">{{ dataItem.document?.documentNumber || 'N/A' }}</span>
              <span class="rta-badge" *ngIf="isRtaDocument(dataItem)" title="Part of RTA {{ rtaNum }} request">RTA</span>
            </div>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="document.title" title="Title" [width]="250">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.document?.title || 'N/A' }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="document.currentRevision" title="Revision" [width]="80">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.document?.currentRevision || '-' }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="document.lifecycleState" title="State" [width]="110">
          <ng-template kendoGridCellTemplate let-dataItem>
            <fv-lifecycle-badge 
              *ngIf="dataItem.document?.lifecycleState !== undefined"
              [state]="dataItem.document.lifecycleState"
              [size]="'small'">
            </fv-lifecycle-badge>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column title="Files" [width]="180">
          <ng-template kendoGridCellTemplate let-dataItem>
            <div class="file-pills">
              <span
                *ngFor="let file of getDocumentFiles(dataItem)"
                class="file-pill"
                [attr.data-ext]="(file.fileExtension || '').replace('.', '').toLowerCase()"
                [title]="file.fileName + '.' + (file.fileExtension || '').replace('.', '')"
                (click)="onDownloadFile(file)">
                {{ (file.fileExtension || '?').replace('.', '').toUpperCase() }}
              </span>
              <span class="no-files" *ngIf="getDocumentFiles(dataItem).length === 0">—</span>
            </div>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column title="Actions" [width]="160">
          <ng-template kendoGridCellTemplate let-dataItem>
            <div class="action-buttons">
              <button kendoButton fillMode="flat" [size]="'small'" (click)="onView(dataItem)" title="View" aria-label="View">
                <kendo-icon name="eye"></kendo-icon>
              </button>
              <button kendoButton fillMode="flat" [size]="'small'" (click)="onDownload(dataItem)" title="Download" aria-label="Download">
                <kendo-icon name="download"></kendo-icon>
              </button>
              <button
                kendoButton
                fillMode="flat"
                [size]="'small'"
                [disabled]="!canEdit || uploadingLinkId === dataItem.id"
                (click)="onUpload(dataItem)"
                title="Upload files"
                aria-label="Upload files">
                <kendo-icon [name]="uploadingLinkId === dataItem.id ? 'loading' : 'upload'"></kendo-icon>
              </button>
              <button 
                kendoButton 
                fillMode="flat"
                [size]="'small'" 
                [disabled]="!canEdit"
                (click)="onUnlink(dataItem)" 
                title="Unlink"
                aria-label="Unlink"
                class="btn-danger">
                <kendo-icon name="unlink-horizontal"></kendo-icon>
              </button>
            </div>
          </ng-template>
        </kendo-grid-column>
      </kendo-grid>

      <!-- Hidden file input for dropzone browse -->
      <input #dropzoneFileInput type="file" hidden multiple
             accept=".tiff,.tif,.prt,.pdf,.dwg,.html,.htm"
             (change)="onDropzoneFilesAdded($event)">

      <!-- Upload Dropzone Dialog -->
      <kendo-dialog
        *ngIf="dropzoneLink"
        [title]="'Upload Files — ' + (dropzoneLink.document?.documentNumber || 'Document')"
        (close)="closeDropzone()"
        [width]="560">

        <div class="dropzone-body">
          <!-- Drop area -->
          <div class="dropzone"
               [class.dropzone-active]="isDragOver"
               (dragover)="onDragOver($event)"
               (dragleave)="onDragLeave($event)"
               (drop)="onDrop($event)"
               (click)="dropzoneFileInput.click()">
            <kendo-icon name="upload" [size]="'xlarge'" class="dropzone-icon"></kendo-icon>
            <div class="dropzone-text">Drag &amp; drop files here</div>
            <div class="dropzone-hint">or click to browse</div>
            <div class="dropzone-accept">.tiff, .tif, .prt, .pdf, .dwg, .html, .htm</div>
          </div>

          <!-- Staged file list -->
          <div class="staged-files" *ngIf="stagedFiles.length > 0">
            <div class="staged-header">
              <span>{{ stagedFiles.length }} file{{ stagedFiles.length > 1 ? 's' : '' }} ready</span>
              <button kendoButton fillMode="flat" [size]="'small'" (click)="clearStagedFiles()">Clear all</button>
            </div>
            <div class="staged-file" *ngFor="let f of stagedFiles; let i = index">
              <span class="staged-file-name" [title]="f.name">{{ f.name }}</span>
              <span class="staged-file-size">{{ formatFileSize(f.size) }}</span>
              <button kendoButton fillMode="flat" [size]="'small'" (click)="removeStagedFile(i)" class="btn-danger" aria-label="Remove">
                <kendo-icon name="x"></kendo-icon>
              </button>
            </div>
          </div>

          <!-- Validation error -->
          <div class="dropzone-error" *ngIf="dropzoneError">
            {{ dropzoneError }}
          </div>
        </div>

        <kendo-dialog-actions>
          <button kendoButton [look]="'flat'" (click)="closeDropzone()">Cancel</button>
          <button kendoButton [themeColor]="'primary'"
                  [disabled]="stagedFiles.length === 0 || uploadingLinkId !== null"
                  (click)="uploadStagedFiles()">
            Upload {{ stagedFiles.length }} file{{ stagedFiles.length > 1 ? 's' : '' }}
          </button>
        </kendo-dialog-actions>
      </kendo-dialog>

      <!-- Upload error banner -->
      <div class="upload-error" *ngIf="uploadError">
        <span>{{ uploadError }}</span>
        <button kendoButton fillMode="flat" [size]="'small'" (click)="dismissUploadError()">✕</button>
      </div>

      <!-- Upload progress panel -->
      <div class="upload-progress-panel" *ngIf="activeUploads.length > 0">
        <div class="upload-progress-header">
          <kendo-icon name="upload"></kendo-icon>
          <span>Uploading {{ activeUploads.length }} file{{ activeUploads.length > 1 ? 's' : '' }}</span>
        </div>
        <div class="upload-progress-item" *ngFor="let u of activeUploads">
          <div class="upload-file-info">
            <span class="upload-file-name" [title]="u.fileName">{{ u.fileName }}</span>
            <span class="upload-file-status" [attr.data-status]="u.status">
              {{ u.status === 'uploading' ? (u.progress | number:'1.0-0') + '%' : u.status }}
            </span>
          </div>
          <kendo-progressbar
            [value]="u.progress"
            [max]="100"
            [animation]="true"
            [label]="{ visible: false }"
            [ngClass]="{ 'upload-failed': u.status === 'failed', 'upload-done': u.status === 'completed' }">
          </kendo-progressbar>
          <div class="upload-error-detail" *ngIf="u.error">{{ u.error }}</div>
        </div>
      </div>

      <!-- Add/Link Dialog -->
      <kendo-dialog
        *ngIf="showAddDialog"
        title="Link Document"
        (close)="closeAddDialog()"
        [width]="500">
        <div class="dialog-content">
          <p class="dialog-hint">Select a document to link to this part</p>
          
          <!-- TODO: Add document search/selection dropdown -->
          <div class="form-group">
            <label>Document Number</label>
            <input kendoTextBox [(ngModel)]="newLinkDocumentNumber" placeholder="Enter document number" />
          </div>

          <div class="form-group">
            <label>Link Type</label>
            <kendo-dropdownlist
              [(ngModel)]="newLinkType"
              [data]="linkTypeOptions"
              textField="label"
              valueField="value">
            </kendo-dropdownlist>
          </div>

          <div class="form-group">
            <label>Purpose (Optional)</label>
            <input kendoTextBox [(ngModel)]="newLinkPurpose" placeholder="Describe the purpose of this link" />
          </div>
        </div>

        <kendo-dialog-actions>
          <button kendoButton [look]="'flat'" (click)="closeAddDialog()">Cancel</button>
          <button kendoButton [themeColor]="'primary'" (click)="confirmAddLink()" [disabled]="!newLinkDocumentNumber">
            Link
          </button>
        </kendo-dialog-actions>
      </kendo-dialog>

      <!-- Unlink Confirmation Dialog -->
      <kendo-dialog
        *ngIf="showUnlinkDialog"
        title="Confirm Unlink"
        (close)="closeUnlinkDialog()"
        [width]="400">
        <p>Are you sure you want to unlink this document?</p>
        <p><strong>{{ unlinkTarget?.document?.documentNumber }} - {{ unlinkTarget?.document?.title }}</strong></p>

        <kendo-dialog-actions>
          <button kendoButton [look]="'flat'" (click)="closeUnlinkDialog()">Cancel</button>
          <button kendoButton [themeColor]="'error'" (click)="confirmUnlink()">
            Unlink
          </button>
        </kendo-dialog-actions>
      </kendo-dialog>
    </div>
  `,
  styles: [`
    .document-links-container {
      display: flex;
      flex-direction: column;
      height: 100%;
      background: var(--fv-surface);
    }

    .toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px;
      border-bottom: 1px solid var(--fv-border);
      background: var(--fv-surface-hover);
    }

    .toolbar-left {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .title {
      font-size: 16px;
      font-weight: 600;
      color: var(--fv-text);
    }

    .count {
      font-size: 14px;
      color: var(--fv-text-secondary);
    }

    .toolbar-right {
      display: flex;
      gap: 8px;
    }

    .loading {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 48px;
      gap: 16px;
      color: var(--fv-text-secondary);
    }

    .empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 48px;
      text-align: center;
    }

    .empty-icon {
      margin-bottom: 16px;
      opacity: 0.5;
      color: var(--fv-text-secondary);
    }

    .empty-text {
      font-size: 18px;
      font-weight: 500;
      color: var(--fv-text);
      margin-bottom: 8px;
    }

    .empty-hint {
      font-size: 14px;
      color: var(--fv-text-secondary);
    }

    .document-links-grid {
      flex: 1;
      overflow: auto;
    }

    .doc-cell {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .doc-icon {
      color: var(--fv-text-secondary);
    }

    .doc-number {
      font-family: 'Consolas', 'Monaco', monospace;
      font-weight: 500;
    }

    .rta-badge {
      display: inline-block;
      padding: 1px 6px;
      border-radius: 3px;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.5px;
      background: #fff3cd;
      color: #856404;
      border: 1px solid #ffc107;
      line-height: 1.4;
    }

    .link-type-badge {
      display: inline-block;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 500;
      text-transform: uppercase;
    }

    .link-type-badge[data-type="0"] { /* Primary */
      background: var(--fv-status-info-bg);
      color: var(--fv-status-info-fg);
    }

    .link-type-badge[data-type="1"] { /* Reference */
      background: var(--fv-status-purple-bg);
      color: var(--fv-status-purple-fg);
    }

    .link-type-badge[data-type="2"] { /* Specification */
      background: var(--fv-status-success-bg);
      color: var(--fv-status-success-fg);
    }

    .link-type-badge[data-type="3"] { /* Test Data */
      background: var(--fv-status-warning-bg);
      color: var(--fv-status-warning-fg);
    }

    .action-buttons {
      display: flex;
      gap: 4px;
    }

    .file-pills {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      align-items: center;
    }

    .file-pill {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.3px;
      cursor: pointer;
      transition: opacity 0.15s;
      background: var(--fv-surface-hover, #e9ecef);
      color: var(--fv-text, #333);
      border: 1px solid var(--fv-border, #dee2e6);
    }

    .file-pill:hover {
      opacity: 0.75;
    }

    .file-pill[data-ext="pdf"] {
      background: #fce4e4;
      color: #c0392b;
      border-color: #f5c6cb;
    }

    .file-pill[data-ext="tiff"],
    .file-pill[data-ext="tif"] {
      background: #e8f5e9;
      color: #2e7d32;
      border-color: #c8e6c9;
    }

    .file-pill[data-ext="prt"] {
      background: #e3f2fd;
      color: #1565c0;
      border-color: #bbdefb;
    }

    .file-pill[data-ext="dwg"] {
      background: #fff3e0;
      color: #e65100;
      border-color: #ffe0b2;
    }

    .file-pill[data-ext="html"],
    .file-pill[data-ext="htm"] {
      background: #ede7f6;
      color: #5e35b1;
      border-color: #d1c4e9;
    }

    .no-files {
      color: var(--fv-text-secondary);
      font-size: 13px;
    }

    .btn-danger { color: #dc3545; }

    .upload-error {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 16px;
      background: #fce4e4;
      color: #dc3545;
      font-size: 13px;
      border-top: 1px solid #f5c6cb;
    }

    .dialog-content {
      padding: 16px 0;
    }

    .dialog-hint {
      margin-bottom: 16px;
      color: var(--fv-text-secondary);
      font-size: 14px;
    }

    .form-group {
      margin-bottom: 16px;
    }

    .form-group label {
      display: block;
      margin-bottom: 4px;
      font-weight: 500;
      font-size: 14px;
      color: var(--fv-text);
    }

    .form-group input,
    .form-group kendo-dropdownlist {
      width: 100%;
    }

    .upload-progress-panel {
      border-top: 1px solid var(--fv-border, #dee2e6);
      background: var(--fv-surface-hover, #f8f9fa);
      padding: 12px 16px;
    }

    .upload-progress-header {
      display: flex;
      align-items: center;
      gap: 6px;
      font-weight: 600;
      font-size: 13px;
      margin-bottom: 10px;
      color: var(--fv-text, #333);
    }

    .upload-progress-item {
      margin-bottom: 8px;
    }

    .upload-progress-item:last-child {
      margin-bottom: 0;
    }

    .upload-file-info {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 4px;
    }

    .upload-file-name {
      font-size: 12px;
      font-family: 'Consolas', 'Monaco', monospace;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 70%;
      color: var(--fv-text, #333);
    }

    .upload-file-status {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
    }

    .upload-file-status[data-status="uploading"] { color: #0d6efd; }
    .upload-file-status[data-status="completed"] { color: #198754; }
    .upload-file-status[data-status="failed"] { color: #dc3545; }
    .upload-file-status[data-status="pending"] { color: #6c757d; }

    .upload-failed ::ng-deep .k-progressbar-value { background: #dc3545; }
    .upload-done ::ng-deep .k-progressbar-value { background: #198754; }

    .upload-error-detail {
      font-size: 11px;
      color: #dc3545;
      margin-top: 2px;
    }

    /* Dropzone styles */
    .dropzone-body {
      padding: 8px 0;
    }

    .dropzone {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 32px 16px;
      border: 2px dashed var(--fv-border, #ced4da);
      border-radius: 8px;
      background: var(--fv-surface, #fff);
      cursor: pointer;
      transition: border-color 0.2s, background 0.2s;
      user-select: none;
    }

    .dropzone:hover {
      border-color: #0d6efd;
      background: #f0f6ff;
    }

    .dropzone-active {
      border-color: #0d6efd;
      background: #e7f1ff;
      border-style: solid;
    }

    .dropzone-icon {
      color: #6c757d;
      margin-bottom: 8px;
    }

    .dropzone-active .dropzone-icon {
      color: #0d6efd;
    }

    .dropzone-text {
      font-size: 15px;
      font-weight: 600;
      color: var(--fv-text, #333);
    }

    .dropzone-hint {
      font-size: 13px;
      color: var(--fv-text-secondary, #6c757d);
      margin-top: 2px;
    }

    .dropzone-accept {
      font-size: 11px;
      color: var(--fv-text-secondary, #adb5bd);
      margin-top: 8px;
    }

    .staged-files {
      margin-top: 16px;
      border: 1px solid var(--fv-border, #dee2e6);
      border-radius: 6px;
      overflow: hidden;
    }

    .staged-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 12px;
      background: var(--fv-surface-hover, #f8f9fa);
      font-size: 13px;
      font-weight: 600;
      color: var(--fv-text, #333);
      border-bottom: 1px solid var(--fv-border, #dee2e6);
    }

    .staged-file {
      display: flex;
      align-items: center;
      padding: 6px 12px;
      gap: 8px;
      border-bottom: 1px solid var(--fv-border, #f0f0f0);
    }

    .staged-file:last-child {
      border-bottom: none;
    }

    .staged-file-name {
      flex: 1;
      font-size: 13px;
      font-family: 'Consolas', 'Monaco', monospace;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .staged-file-size {
      font-size: 11px;
      color: var(--fv-text-secondary, #6c757d);
      white-space: nowrap;
    }

    .dropzone-error {
      margin-top: 12px;
      padding: 8px 12px;
      background: #fce4e4;
      color: #dc3545;
      font-size: 13px;
      border-radius: 4px;
    }
  `]
})
export class DocumentLinksComponent implements OnInit, OnChanges, OnDestroy {
  private apiService = inject(FileVaultApiService);

  @Input() partId!: string;
  @Input() partNumber?: string;
  @Input() rtaNum?: number;
  @Input() canEdit: boolean = false;
  @Input() showToolbar: boolean = true;
  @Input() autoLoad: boolean = true;

  @Output() actionClick = new EventEmitter<DocumentLinkActionEvent>();
  @Output() addLink = new EventEmitter<AddDocumentLinkEvent>();
  @Output() removeLink = new EventEmitter<string>(); // link ID
  @Output() documentUnlinked = new EventEmitter<string>(); // document number

  links: PartDocumentLink[] = [];
  gridData: GridDataResult = { data: [], total: 0 };
  isLoading = false;
  selectedLink: PartDocumentLink | null = null;

  // Add Dialog
  showAddDialog = false;
  newLinkDocumentNumber = '';
  newLinkType: LinkType = LinkType.Reference;
  newLinkPurpose = '';

  // Unlink Dialog
  showUnlinkDialog = false;
  unlinkTarget: PartDocumentLink | null = null;

  // Upload state
  uploadingLinkId: string | null = null;
  uploadError: string | null = null;
  activeUploads: ChunkUploadInfo[] = [];
  private uploadSubs: Subscription[] = [];

  // Dropzone state
  dropzoneLink: PartDocumentLink | null = null;
  stagedFiles: File[] = [];
  isDragOver = false;
  dropzoneError: string | null = null;

  private readonly ACCEPTED_EXTENSIONS = ['.tiff', '.tif', '.prt', '.pdf', '.dwg', '.html', '.htm'];

  // File data per document (keyed by documentId)
  fileMap: Record<string, FileVaultItem[]> = {};

  // RTA drawing number tracking — document numbers pulled for this RTA request
  rtaDocumentNumbers: Set<string> = new Set();

  linkTypeOptions = [
    { label: 'Primary', value: LinkType.Primary },
    { label: 'Reference', value: LinkType.Reference },
    { label: 'Specification', value: LinkType.Specification },
    { label: 'Test Data', value: LinkType.TestData }
  ];

  ngOnInit(): void {
    if (this.autoLoad && this.partId) {
      this.loadLinks();
    }
  }

  ngOnDestroy(): void {
    this.uploadSubs.forEach(s => s.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['partId'] && !changes['partId'].firstChange && this.autoLoad) {
      this.loadLinks();
    }
  }

  loadLinks(): void {
    if (!this.partId) {
      return;
    }

    this.isLoading = true;

    // If rtaNum is provided, fetch RTA drawing numbers first, then load documents
    if (this.rtaNum) {
      this.apiService.getDrawingNumbersByRta(this.rtaNum).subscribe({
        next: (rtaResults) => {
          this.rtaDocumentNumbers = new Set(
            rtaResults
              .map(r => r.drawingNumber?.toLowerCase())
              .filter((n): n is string => !!n)
          );
          this.loadDocuments();
        },
        error: () => {
          this.rtaDocumentNumbers = new Set();
          this.loadDocuments();
        }
      });
    } else {
      this.rtaDocumentNumbers = new Set();
      this.loadDocuments();
    }
  }

  private loadDocuments(): void {
    this.apiService.getLinkedDocuments(this.partId).subscribe({
      next: (documents) => {
        this.links = documents.map(doc => ({
          id: '',
          partId: this.partId,
          documentId: doc.id,
          linkType: LinkType.Reference,
          purpose: undefined,
          effectiveFrom: undefined,
          effectiveTo: undefined,
          createdAt: new Date(),
          createdBy: '',
          document: doc
        }));

        // Sort: RTA-matched documents first, then alphabetically by document number
        if (this.rtaDocumentNumbers.size > 0) {
          this.links.sort((a, b) => {
            const aIsRta = this.isRtaDocument(a) ? 0 : 1;
            const bIsRta = this.isRtaDocument(b) ? 0 : 1;
            if (aIsRta !== bIsRta) return aIsRta - bIsRta;
            return (a.document?.documentNumber || '').localeCompare(b.document?.documentNumber || '');
          });
        }
        
        this.updateGrid();
        this.isLoading = false;

        this.loadFileCounts(documents);
      },
      error: (error) => {
        console.error('Error loading linked documents:', error);
        this.isLoading = false;
      }
    });
  }

  updateGrid(): void {
    this.gridData = {
      data: this.links,
      total: this.links.length
    };
  }

  refresh(): void {
    this.loadLinks();
  }

  onSelectionChange(event: any): void {
    this.selectedLink = event.selectedRows[0]?.dataItem || null;
  }

  // Dialog Actions
  openAddDialog(): void {
    this.newLinkDocumentNumber = '';
    this.newLinkType = LinkType.Reference;
    this.newLinkPurpose = '';
    this.showAddDialog = true;
  }

  closeAddDialog(): void {
    this.showAddDialog = false;
  }

  confirmAddLink(): void {
    this.addLink.emit({
      documentId: this.newLinkDocumentNumber, // In reality, would be ID from search
      linkType: this.newLinkType,
      purpose: this.newLinkPurpose || undefined
    });
    this.closeAddDialog();
  }

  // Grid Actions
  onView(link: PartDocumentLink): void {
    this.actionClick.emit({
      action: 'view',
      link,
      document: link.document
    });
  }

  onDownload(link: PartDocumentLink): void {
    this.actionClick.emit({
      action: 'download',
      link,
      document: link.document
    });
  }

  onUnlink(link: PartDocumentLink): void {
    this.unlinkTarget = link;
    this.showUnlinkDialog = true;
  }

  closeUnlinkDialog(): void {
    this.showUnlinkDialog = false;
    this.unlinkTarget = null;
  }

  confirmUnlink(): void {
    if (this.unlinkTarget) {
      const docNumber = this.unlinkTarget.document?.documentNumber || '';
      this.removeLink.emit(this.unlinkTarget.id);

      // Remove from local list immediately
      this.links = this.links.filter(l => l !== this.unlinkTarget);
      this.updateGrid();

      if (docNumber) {
        this.documentUnlinked.emit(docNumber);
      }
      this.closeUnlinkDialog();
    }
  }

  // Upload / Dropzone Actions
  onUpload(link: PartDocumentLink): void {
    this.dropzoneLink = link;
    this.stagedFiles = [];
    this.dropzoneError = null;
    this.isDragOver = false;
  }

  closeDropzone(): void {
    this.dropzoneLink = null;
    this.stagedFiles = [];
    this.dropzoneError = null;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files?.length) {
      this.addFilesToStage(Array.from(files));
    }
  }

  onDropzoneFilesAdded(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.addFilesToStage(Array.from(input.files));
    }
    input.value = '';
  }

  private addFilesToStage(files: File[]): void {
    this.dropzoneError = null;

    // Filter by accepted extensions
    const rejected: string[] = [];
    const accepted: File[] = [];
    for (const f of files) {
      const ext = '.' + f.name.split('.').pop()?.toLowerCase();
      if (this.ACCEPTED_EXTENSIONS.includes(ext)) {
        // Avoid duplicates by name
        if (!this.stagedFiles.some(s => s.name === f.name)) {
          accepted.push(f);
        }
      } else {
        rejected.push(f.name);
      }
    }

    if (rejected.length) {
      this.dropzoneError = `Unsupported file type: ${rejected.join(', ')}`;
    }

    this.stagedFiles = [...this.stagedFiles, ...accepted];
  }

  removeStagedFile(index: number): void {
    this.stagedFiles = this.stagedFiles.filter((_, i) => i !== index);
  }

  clearStagedFiles(): void {
    this.stagedFiles = [];
    this.dropzoneError = null;
  }

  uploadStagedFiles(): void {
    if (!this.dropzoneLink || !this.stagedFiles.length) return;

    const drawingNumber = this.dropzoneLink.document?.documentNumber || '';
    const linkId = this.dropzoneLink.id;

    // Validate filenames contain the drawing number
    if (drawingNumber) {
      const invalid = this.stagedFiles.filter(
        f => !f.name.toLowerCase().includes(drawingNumber.toLowerCase())
      );
      if (invalid.length) {
        this.dropzoneError = `Filename must contain "${drawingNumber}": ${invalid.map(f => f.name).join(', ')}`;
        return;
      }
    }

    this.uploadError = null;
    this.uploadingLinkId = linkId;
    this.closeDropzone();

    let completedCount = 0;
    const totalFiles = this.stagedFiles.length;
    const filesToUpload = [...this.stagedFiles];

    for (const file of filesToUpload) {
      const upload$ = this.apiService.uploadFileChunked(file, {
        metadata: {
          autoCreateDocument: 'true',
          documentNumber: drawingNumber
        }
      });

      const sub = upload$.subscribe({
        next: (info: ChunkUploadInfo) => {
          const idx = this.activeUploads.findIndex(u => u.fileId === info.fileId);
          if (idx >= 0) {
            this.activeUploads[idx] = info;
          } else {
            this.activeUploads.push(info);
          }

          if (info.status === UploadStatus.Completed) {
            completedCount++;
            if (completedCount === totalFiles) {
              this.uploadingLinkId = null;
              this.loadLinks();
              setTimeout(() => {
                this.activeUploads = this.activeUploads.filter(u => u.status !== UploadStatus.Completed);
              }, 3000);
            }
          }

          if (info.status === UploadStatus.Failed) {
            this.uploadError = info.error || 'Upload failed';
          }
        },
        error: (err) => {
          this.uploadingLinkId = null;
          this.uploadError = err?.error?.Error ?? err?.message ?? 'Upload failed';
        }
      });

      this.uploadSubs.push(sub);
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  dismissUploadError(): void {
    this.uploadError = null;
  }

  // File helpers
  getDocumentFiles(link: PartDocumentLink): FileVaultItem[] {
    return link.documentId ? (this.fileMap[link.documentId] ?? []) : [];
  }

  onDownloadFile(file: FileVaultItem): void {
    this.apiService.downloadFile(file.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.fileName + (file.fileExtension ? '.' + file.fileExtension.replace(/^\./, '') : '');
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Download failed:', err);
      }
    });
  }

  private loadFileCounts(documents: Document[]): void {
    this.fileMap = {};

    if (!documents.length) return;

    const fileRequests: Record<string, ReturnType<typeof this.apiService.getDocumentVersionFiles>> = {};
    for (const doc of documents) {
      if (doc.latestVersionId) {
        fileRequests[doc.id] = this.apiService.getDocumentVersionFiles(doc.latestVersionId);
      }
    }

    const keys = Object.keys(fileRequests);
    if (!keys.length) return;

    forkJoin(fileRequests).subscribe({
      next: (results) => {
        for (const docId of Object.keys(results)) {
          this.fileMap[docId] = results[docId];
        }
        // Re-emit gridData so the grid picks up the updated fileMap
        this.updateGrid();
      },
      error: () => {
        // On error, ensure all docs have an empty array
        for (const doc of documents) {
          this.fileMap[doc.id] = this.fileMap[doc.id] ?? [];
        }
        this.updateGrid();
      }
    });
  }

  // Helper Methods

  /** Remove a document from the local list by document number (called externally when drawing-number-list unlinks) */
  removeByDocumentNumber(documentNumber: string): void {
    this.links = this.links.filter(
      l => l.document?.documentNumber?.toLowerCase() !== documentNumber.toLowerCase()
    );
    this.updateGrid();
  }

  isRtaDocument(link: PartDocumentLink): boolean {
    if (!this.rtaDocumentNumbers.size) return false;
    const docNum = link.document?.documentNumber?.toLowerCase();
    return !!docNum && this.rtaDocumentNumbers.has(docNum);
  }

  getLinkTypeLabel(linkType?: LinkType): string {
    if (linkType === undefined) return 'Unknown';
    const option = this.linkTypeOptions.find(o => o.value === linkType);
    return option?.label || 'Unknown';
  }

  getDocumentIcon(document?: Document): string {
    if (!document) return 'file';
    
    switch (document.documentType) {
      case DocumentType.Drawing:
        return 'image';
      case DocumentType.Specification:
        return 'file-txt';
      case DocumentType.Manual:
        return 'book';
      case DocumentType.Procedure:
        return 'paste-plain-text';
      case DocumentType.TestReport:
        return 'clipboard';
      case DocumentType.Certificate:
        return 'check-circle';
      case DocumentType.CADModel:
        return 'shape';
      case DocumentType.Image:
        return 'photo-camera';
      default:
        return 'file';
    }
  }

  getDocumentTypeLabel(docType?: DocumentType): string {
    if (docType === undefined) return 'Unknown';
    
    switch (docType) {
      case DocumentType.Drawing: return 'Drawing';
      case DocumentType.Specification: return 'Specification';
      case DocumentType.Manual: return 'Manual';
      case DocumentType.Procedure: return 'Procedure';
      case DocumentType.TestReport: return 'Test Report';
      case DocumentType.Certificate: return 'Certificate';
      case DocumentType.CADModel: return 'CAD Model';
      case DocumentType.Image: return 'Image';
      default: return 'Other';
    }
  }
}
